import { defined, defaultValue } from './helpers';

class Marker {
  async load(hud, pos, desc, headless) {
    const canvas = document.createElement('canvas');
    canvas.height = 220;
    canvas.width = 200;
    this.text = desc.text;
    this.pos = pos;

    if (!headless) {
      const ctx = canvas.getContext('2d');
      drawPin({ x: canvas.width / 2, y: canvas.height - 10, color: desc.color }, ctx);
      addLabel(canvas, ctx, desc.text, desc.subtext);

      this.hudElement = hud.createElement(`DistanceMarker${desc.text}`);
      this.hudElement.setWorldPos(pos);
      this.hudElement.setCanvas(canvas);
      this.hudElement.setWorldHeight(200.0);
    }

    this.ready = true;
  }

  destroy(hud) {
    hud.removeElement(this.hudElement);
  }
}

const addLabel = (canvas, ctx, text, subtext) => {
  ctx.fillStyle = '#D3D3D3';
  ctx.font = '55px Rubik Mono One, Arial Black, Arial, sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText(text, canvas.width / 2, canvas.height / 2);
  if (!subtext) return;
  ctx.font = '25px Rubik Mono One, Arial Black, Arial, sans-serif';
  ctx.fillText(subtext, canvas.width / 2, 140);
};

const drawPin = (pin, ctx) => {
  const bcurve = 140;
  // TODO: The Bezier curve could use some work
  ctx.save();
  ctx.translate(pin.x, pin.y);
  ctx.beginPath();
  ctx.moveTo(0, 0);
  ctx.bezierCurveTo(0, 0, -bcurve + 15, -bcurve, 0, -bcurve - 25);
  ctx.bezierCurveTo(bcurve - 6, -bcurve, -15, 15, 0, 0);
  ctx.arc(0, -110, 55, 0, 2 * Math.PI);
  ctx.fillStyle = pin.color;
  ctx.fill();

  ctx.beginPath();
  ctx.globalCompositeOperation = 'destination-out';
  ctx.arc(0, -110, 50, 0, 2 * Math.PI);
  ctx.fill();
  ctx.restore();
};

/**
 * Generate Markers (a MediaItem) at specific distances along a given track.
 * Markers will be re-generated upon switching of units (e.g. km vs. miles)
 */
export default class DistanceMarkers {
  constructor(hud, headless) {
    this.markers = [];
    this.hud = hud;
    this.headless = headless;
  }

  onSceneLoad(scene) {
    if (defined(scene.distanceMarkers)) {
      this.units = undefined;

      this.settings = scene.distanceMarkers;
      this.settings.frequency = defaultValue(this.settings.frequency, 5);
      this.settings.color = defaultValue(this.settings.color, 'rgb(244, 238, 66)');
      this.settings.height = defaultValue(this.settings.height, 200);

      this.trackId = scene.course ? '__courseActivity' : Object.keys(scene.activities)[0];
    }
  }

  update(tracks, state) {
    if (!this.trackId) {
      return;
    }

    // If the user changes units, reinitialize all of the markers
    // @NOTE: This will also trigger a call to generateMarkers on first update
    if (this.units !== state.stats.units) {
      this.units = state.stats.units;
      const track = tracks.getTrack(this.trackId);
      this.deleteMarkers();
      this.generateMarkersForTrack(track, this.units);
    }
  }

  onPointsAdded(tracks) {
    if (!this.trackId) {
      return;
    }

    const track = tracks.getTrack(this.trackId);

    // If the track got new points, just regenerate all the markers
    // @TODO: Only handle new points
    if (track.dirty) {
      this.deleteMarkers();
      this.generateMarkersForTrack(track, this.units);
    }
  }

  deleteMarkers() {
    if (!this.headless) {
      this.markers.forEach(m => {
        m.destroy(this.hud);
      });
    }
    this.markers = [];
  }

  generateMarkersForTrack(track, units) {
    // Find the marker positions along the track based on the specified frequency
    const unitsScale = units === 'metric' ? 1 : 0.621371;
    const interval = (this.settings.frequency * 1000) / unitsScale;
    const result = { x: 0.0, y: 0.0, z: 0.0 };
    const points = track.sampleInterval({ cumulative: interval }, result);

    // Create a marker for each point
    for (let i = 0; i < points.length; i++) {
      const pos = points[i];
      if (pos) {
        const marker = new Marker();
        const distance = i * this.settings.frequency;
        const desc = {
          color: this.settings.color,
          text: distance.toString(),
          subtext: units === 'metric' ? 'km' : 'mi',
        };

        marker.load(this.hud, pos, desc, this.headless);

        this.markers.push(marker);
      }
    }
  }
}
